import Storage from '@/utils/wrappers/Storage';

// v2 keys include dashboardUserId so drafts are not shared across accounts
// on the same browser.
export const MESSAGE_DRAFTS_STORAGE_KEY = 'messageDrafts.v2';

export const MESSAGE_DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export type MessageDraftKeyParts = {
  dashboardUserId: string;
  brandId: string;
  conversationId: string;
  campaignId?: string | null;
};

export type MessageDraftEntry = {
  text: string;
  updatedAt: number;
};

export type MessageDraftMap = Record<string, MessageDraftEntry>;

export function draftKey({
  dashboardUserId,
  brandId,
  conversationId,
  campaignId,
}: MessageDraftKeyParts): string {
  return `${dashboardUserId}:${brandId}:${conversationId}:${campaignId ?? ''}`;
}

function isDraftMap(value: unknown): value is MessageDraftMap {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readMap(): MessageDraftMap {
  try {
    const raw = Storage.getItemSync(MESSAGE_DRAFTS_STORAGE_KEY);
    if (!isDraftMap(raw)) {
      return {};
    }
    return raw;
  } catch {
    return {};
  }
}

function writeMap(map: MessageDraftMap): void {
  // Storage.setItem is async but may be mocked without a thenable in tests.
  Promise.resolve(Storage.setItem(MESSAGE_DRAFTS_STORAGE_KEY, map)).catch(
    () => {
      // localStorage can fail in private mode; drafts are best-effort.
    }
  );
}

export function pruneExpired(
  map: MessageDraftMap,
  now: number = Date.now()
): MessageDraftMap {
  const next: MessageDraftMap = {};
  Object.entries(map).forEach(([key, entry]) => {
    if (
      entry &&
      typeof entry.text === 'string' &&
      typeof entry.updatedAt === 'number' &&
      now - entry.updatedAt <= MESSAGE_DRAFT_TTL_MS &&
      entry.text.trim() !== ''
    ) {
      next[key] = entry;
    }
  });
  return next;
}

function hasDraftIdentity(parts: MessageDraftKeyParts): boolean {
  return Boolean(
    parts.dashboardUserId && parts.brandId && parts.conversationId
  );
}

export function getDraft(parts: MessageDraftKeyParts): string | null {
  if (!hasDraftIdentity(parts)) {
    return null;
  }
  const key = draftKey(parts);
  const map = pruneExpired(readMap());
  const entry = map[key];
  return entry?.text ?? null;
}

export function setDraft(parts: MessageDraftKeyParts, text: string): void {
  if (!hasDraftIdentity(parts)) {
    return;
  }
  const key = draftKey(parts);
  const trimmedEmpty = text.trim() === '';
  const map = pruneExpired(readMap());

  if (trimmedEmpty) {
    if (!(key in map)) {
      return;
    }
    delete map[key];
    writeMap(map);
    return;
  }

  map[key] = {text, updatedAt: Date.now()};
  writeMap(map);
}

export function clearDraft(parts: MessageDraftKeyParts): void {
  setDraft(parts, '');
}

export function hasDraft(parts: MessageDraftKeyParts): boolean {
  return getDraft(parts) !== null;
}

/** Returns draft keys that currently have non-empty, non-expired text. */
export function getActiveDraftKeys(): Set<string> {
  return new Set(Object.keys(pruneExpired(readMap())));
}
